import { centsFrom, isEmailAddress, surchargeFor } from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'
import { siteGiftTo, siteOffersAccount } from '../lib/public-router'
import { holdsThePayment, returnedCheckout } from '../lib/returned-checkout'
import { useToday } from '../lib/today'
import { useTRPC } from '../lib/trpc'
import {
  answersForCheckout,
  cardCostNote,
  continueLabel,
} from '../lib/two-step-payment'
import {
  AnswerLine,
  BranchList,
  BranchRow,
  Button,
  Choice,
  type ChoiceOption,
  Field,
  HeldAnswers,
  Notice,
  PaymentFlow,
  PaymentStep,
  Question,
  ReturnedCheckoutNotice,
  SettingBlock,
  Textarea,
  Toggle,
  TotalLine,
  useCheckoutConfirmation,
  useReturnedCheckout,
} from '../ui'
import { PublicNotFound } from '../ui/ErrorPage'
import { PublicShell } from '../ui/PublicShell'
import { AccountLinkRequest } from './AccountLinkRequest'
import { DonateAmountStep } from './DonateAmountStep'
import { DonateNotAccepting } from './DonateNotAccepting'
import { DonateThanks } from './DonateThanks'
import { DonorFields } from './DonorFields'
import { forgetAnswers, GIFT, resumable } from './donate-answers'
import {
  ACCOUNT,
  coverFeeSays,
  donateTitle,
  findDonateItem,
  firstQuestion,
  MEMBERSHIP,
  MENU_ASK,
  money,
  OFTEN,
  type Often,
  thanksFor,
} from './donate-page-helpers'
import { GiveFrame } from './GiveFrame'
import { MembershipBranch } from './MembershipBranch'
import { forgetMembership, recallMembership } from './membership-answers'
import {
  HowOftenStep,
  type StandingGiftAnswers,
  StandingGiftStep,
} from './StandingGiftStep'
import './donate.css'
import {
  givingChoiceOffered,
  isKioskMode,
  kioskHref,
  recallDonateAnswers,
  rememberDonateAnswers,
  useKioskActivity,
  useKioskBusy,
} from './kiosk'

/**
 * The public donate page at `/{shortId}/donate` — one question at a time.
 *
 * Each answer folds to a line carrying what was chosen and the way back to it,
 * and a question is asked only where it applies: a priced thing names its own
 * figure, so the amount is never asked for one; an item that asks for words
 * asks in its own wording; a question with one answer is not a question and
 * folds in already answered.
 *
 * NOTHING here is a list in the code. The first question is derived — a gift,
 * and every drive collecting today — and everything under a gift is the shul's
 * own catalog, published. A fund hard-coded on this page would be a second
 * answer to what a congregation collects for, and wrong the first time one of
 * them changed.
 *
 * A drive is offered here and answered on its own page. What a drive asks — its
 * appeal, its progress, its dedication board — is that page's, so every way in
 * to a campaign reaches the same questions and files the gift the same way.
 */
export function DonatePage({
  orgId,
  onLogin,
  missedCampaign = false,
}: {
  orgId: string
  onLogin?: () => void
  /** Whether the visitor arrived by a campaign address nothing answers to. */
  missedCampaign?: boolean
}) {
  const kiosk = isKioskMode()
  const reportKioskActivity = useKioskActivity(kiosk)
  const kioskBusy = useKioskBusy(kiosk)
  const trpc = useTRPC()
  const org = useQuery(trpc.donation.org.queryOptions({ shortId: orgId }))
  const menu = useQuery(trpc.donation.menu.queryOptions({ shortId: orgId }))
  const drives = useQuery(
    trpc.donation.campaigns.queryOptions({ shortId: orgId }),
  )
  const tiers = useQuery(trpc.donation.tiers.queryOptions({ shortId: orgId }))
  const [completedCheckoutId, showOutcome] = useReturnedCheckout()
  /* Each branch completes with its OWN answers and none of the other's. A
     donor who tried one branch and paid through another has both stored, and
     a thank-you read from the wrong one names a gift they did not make. */
  const completeGift = (checkoutId: string) => {
    setKept(recallDonateAnswers(orgId, kiosk))
    setKeptMembership(null)
    showOutcome(checkoutId)
  }
  const completeMembership = (checkoutId: string) => {
    setKept(null)
    setKeptMembership(kiosk ? null : recallMembership(orgId))
    showOutcome(checkoutId)
  }
  /* A pledge is neither: it is thanked without naming a gift, and nothing
     stored from an earlier attempt may stand in for it. */
  const completePledge = (checkoutId: string) => {
    if (!kiosk) forgetAnswers(orgId)
    if (!kiosk) forgetMembership(orgId)
    setKept(null)
    setKeptMembership(null)
    showOutcome(checkoutId)
  }
  const confirmation = useCheckoutConfirmation({
    onComplete: completeGift,
  })
  const outcome = useQuery({
    ...trpc.donation.checkoutOutcome.queryOptions({
      shortId: orgId,
      checkoutId: completedCheckoutId ?? '',
    }),
    enabled: completedCheckoutId !== null,
  })
  const returned = returnedCheckout({
    checkoutId: completedCheckoutId,
    status: outcome.data?.status,
    isError: outcome.isError,
  })
  const paid = returned === 'paid'
  useEffect(() => {
    if (paid || outcome.isError) kioskBusy.release()
  }, [kioskBusy, outcome.isError, paid])
  const readerToday = useToday()

  /* Stored answers survive a reload or a redirect-based payment method. They
     name the acknowledgement and resume an unfinished gift, then clear only
     after the server reads a paid checkout.
     Read again when a payment completes, because a card completes IN PLACE:
     the answers this page opened with are not the answers it was paid for, and
     a thank-you drawn from them names the wrong gift or none at all. */
  const [kept, setKept] = useState(() => recallDonateAnswers(orgId, kiosk))
  const [keptMembership, setKeptMembership] = useState(() =>
    kiosk ? null : recallMembership(orgId),
  )
  /* A stored answer still thanks the gift it paid for, but only a gift this
     page carries is picked up again: an answer naming a drive belongs to the
     drive's own page. */
  const resume = resumable(kept)
  const again = resume
    ? 'Your answers are below — try again when you are ready.'
    : 'Start again when you are ready.'
  const thankedGift = answersForCheckout(kept, completedCheckoutId)
  const thankedMembership = answersForCheckout(
    keptMembership,
    completedCheckoutId,
  )

  const [branch, setBranch] = useState<string | null>(resume?.branch ?? null)
  /** The menu parents opened so far, outermost first. */
  const [open, setOpen] = useState<string[]>(resume?.open ?? [])
  const [picked, setPicked] = useState<string | null>(
    resume?.chargeItemId ?? null,
  )
  /* What is marked on the menu but not yet committed. Held apart from `picked`
     because picking must not advance the page — see the Choice below. */
  const [pendingItem, setPendingItem] = useState<string | null>(null)
  const [words, setWords] = useState(resume?.description ?? '')
  const [asked, setAsked] = useState(Boolean(resume?.description))
  const [typed, setTyped] = useState('')
  /* A question reopened from its folded line. Reopening asks it again and
     clears nothing: what was answered below it stays held, and only the new
     answer decides which of those still apply. */
  const [reopened, setReopened] = useState<'branch' | 'item' | null>(null)
  const [named, setNamed] = useState<number | null>(resume?.amountCents ?? null)
  /* How often, where the item leaves it to the giver. Null is unanswered. */
  const [often, setOften] = useState<Often | null>(resume?.often ?? null)
  const [pendingOften, setPendingOften] = useState<Often | null>(null)
  const [thankedStanding, setThankedStanding] = useState<
    (StandingGiftAnswers & { checkoutId: string }) | null
  >(null)
  const [coverFee, setCoverFee] = useState(resume?.coverFee ?? false)
  const [saveCard, setSaveCard] = useState(
    kiosk ? false : (resume?.saveCard ?? true),
  )
  const [name, setName] = useState(resume?.donorName ?? '')
  const [email, setEmail] = useState(resume?.donorEmail ?? '')
  /* A standing gift's start, held here so it survives a question above being
     asked again. Null is the org's today. */
  const [startsOn, setStartsOn] = useState<string | null>(null)

  const checkout = useMutation({
    ...trpc.donation.checkout.mutationOptions(),
    /* The stored answers learn which checkout they were opened as, so a
       payment returning from a redirect is thanked from these answers only if
       they are the ones it paid for. */
    onSuccess: ({ checkoutId }) => {
      const stored = recallDonateAnswers(orgId, kiosk)
      if (stored) rememberDonateAnswers(orgId, { ...stored, checkoutId }, kiosk)
    },
  })

  /* A finished payment has no answers left to keep: what they were for is read
     once to name the gift, and then they go, so a reload of the thank-you page
     cannot resume a gift that was already made. */
  useEffect(() => {
    if (!paid) return
    if (!kiosk) forgetAnswers(orgId)
    if (!kiosk) forgetMembership(orgId)
  }, [paid, orgId, kiosk])

  /*
   * The question in hand takes focus when it changes.
   *
   * Answering unmounts the control that was focused, which drops focus to the
   * body: the next key press starts again at the top of the document, and a
   * reader who cannot see the card is told nothing at all — on a page whose
   * whole shape is one question replaced by the next. Focusing the region
   * announces the new question and puts the keyboard inside it.
   *
   * Keyed on the ANSWERS and on which question is reopened, rather than on the
   * stage, so reopening one question from another — Edit on a folded line —
   * moves focus too, and so does answering it again. The first paint
   * is not a change: focusing on arrival would take a reader out of the page
   * they just opened.
   */
  const stepRef = useRef<HTMLDivElement>(null)
  const boundsId = useId()
  const at = `${reopened ?? ''}|${branch ?? ''}|${open.join('>')}|${picked ?? ''}|${asked}|${often ?? ''}|${named ?? ''}`
  const wasAt = useRef<string | null>(null)
  useEffect(() => {
    if (wasAt.current !== null && wasAt.current !== at) stepRef.current?.focus()
    wasAt.current = at
  }, [at])

  /* The first question is DERIVED, so it waits for what derives it. Drawn from
     data still in flight it derives the wrong question — a shul with a drive
     collecting looks, for one paint, like a shul with one branch, and folds the
     donor straight into a menu they did not choose. */
  const waiting =
    org.isLoading || menu.isLoading || drives.isLoading || tiers.isLoading
  if (waiting || org.error || !org.data) {
    return (
      <PublicShell
        name={null}
        orgId={null}
        current="donate"
        onLogin={onLogin}
        kiosk={kiosk}
      >
        {waiting ? (
          <GiveFrame head={<h1 className="give__title">Give</h1>} other={[]}>
            <Notice>Loading…</Notice>
          </GiveFrame>
        ) : (
          <PublicNotFound />
        )}
      </PublicShell>
    )
  }
  const o = org.data
  const to = siteGiftTo(o.name)
  const items = menu.data ?? []
  const collecting = drives.data ?? []
  const offered = tiers.data?.tiers ?? []
  /* The shul's own day, not the browser's: a start date is quoted against it. */
  const shulToday = tiers.data?.today ?? null

  const { branches, driveRows } = firstQuestion(
    o.shortId,
    to,
    offered.length > 0,
    collecting,
    siteOffersAccount(),
    kiosk,
  )
  /* One branch is not a question. It folds in already answered, the way any
     question with a single sensible answer does — and a drive on offer is a
     second answer, even though this page does not hold it. */
  const sole =
    branches.length === 1 && driveRows.length === 0 ? branches[0].key : null
  const known = branches.some((b) => b.key === branch) ? branch : null
  const on = known ?? sole

  const here = open.length ? findDonateItem(items, open[open.length - 1]) : null
  const level = here ? here.children : items
  /* A menu offering one thing is the same case as one branch: the shul has a
     catch-all and nothing else published, and asking which of one is wanted
     spends a question on an answer nobody can give differently. */
  const onlyItem =
    on === GIFT && !here && level.length === 1 && level[0].children.length === 0
      ? level[0]
      : null
  const item = picked ? findDonateItem(items, picked) : onlyItem
  /* A question nobody was asked has nothing to change. Where the shul publishes
     one thing, the answer folded in is the only one there is, and a control
     offering to change it reopens a set of one. */
  const itemWasChosen = item !== null && item !== onlyItem

  /* Three things set the figure themselves, so the question reaches only what
     names none: a priced item takes its price, and everything else asks. */
  const priced = item?.defaultCents ?? null
  const asksAmount = priced === null
  const giftCents = priced ?? named
  const prompt = item?.descriptionPrompt ?? null
  /* An item whose prompt repeats the menu's own question has already had it
     answered by being picked, so what is left to ask is only for a note. */
  const noteAsk = prompt === MENU_ASK ? 'Add a note' : prompt
  /* What the words are called once written. An item's own prompt ("In honor
     of") reads back as itself; the menu's invitation does not. */
  const noteSaid = prompt === MENU_ASK ? 'Note' : prompt

  const membership = on === MEMBERSHIP
  const account = on === ACCOUNT
  /* A branch whose questions this page does not ask: membership's are its
     own component's, and an account is answered by a mailed link. */
  const elsewhere = membership || account

  /* Asked only of an item that leaves it to the giver; every other gift is
     given once, and a question with one answer is not asked. */
  const asksOften =
    (item?.givesStanding ?? false) && givingChoiceOffered('recurring', kiosk)
  const standing = asksOften && often && often !== 'once' ? often : null

  const stage: 'branch' | 'menu' | 'words' | 'often' | 'amount' | 'donor' =
    on === null || reopened === 'branch'
      ? 'branch'
      : on === GIFT && (!item || reopened === 'item')
        ? 'menu'
        : prompt && !asked
          ? 'words'
          : asksOften && often === null
            ? 'often'
            : asksAmount && named === null
              ? 'amount'
              : 'donor'

  /* What the gift was for, as the donor reads it back. The catch-all is the
     shul itself, so it names nothing: "a gift toward General donation" is the
     page reading its own catalog aloud. */
  const forLabel = item && !item.catchAll ? item.label : null

  /* The card cost is a share of one charge. A standing gift is charged by the
     card it leaves, which covers no card cost, so an answer given for a gift
     once does not carry over to it. */
  const covers = coverFee && standing === null
  const feeCents = giftCents && covers ? surchargeFor(giftCents) : 0
  const chargedCents = (giftCents ?? 0) + feeCents
  const checkoutTotalCents = checkout.variables
    ? checkout.variables.amountCents +
      (checkout.variables.coverFee
        ? surchargeFor(checkout.variables.amountCents)
        : 0)
    : null
  const pending = centsFrom(typed)
  const inBounds = (cents: number) =>
    cents >= o.minGiftCents && cents <= o.maxGiftCents
  const ready =
    giftCents !== null &&
    inBounds(giftCents) &&
    name.trim().length > 0 &&
    isEmailAddress(email)

  /* An open checkout was created from the answers as they stood, so Back
     discards it and keeps every answer: the next Continue opens a checkout for
     what the donor then says, and the one they left can never be confirmed. */
  const discardCheckout = () => {
    checkout.reset()
    confirmation.reset()
  }

  const chooseOften = (answer: Often) => {
    discardCheckout()
    setOften(answer)
  }

  const completeStanding = (
    checkoutId: string,
    answers: StandingGiftAnswers,
  ) => {
    /* Thanked from its own answers, like a pledge: nothing stored stands in. */
    setThankedStanding({ ...answers, checkoutId })
    completePledge(checkoutId)
  }

  const reset = () => {
    discardCheckout()
    setOften(null)
    setPendingOften(null)
    setBranch(null)
    setOpen([])
    setPicked(null)
    setPendingItem(null)
    setWords('')
    setAsked(false)
    setTyped('')
    setNamed(null)
    setReopened(null)
  }

  /* An answer given again keeps everything below it that still means the same
     thing. A different branch takes the menu path with it, since an item
     belongs to the gift and not to membership, and the words, which answered a
     question the new branch does not ask. The amount stands: every branch that
     asks one asks it under the same bounds. */
  const chooseBranch = (key: string) => {
    discardCheckout()
    if (key !== on) {
      setOpen([])
      setPicked(null)
      setPendingItem(null)
      setWords('')
      setAsked(false)
      dropPrice()
    }
    setBranch(key)
    setReopened(null)
  }

  /* Another item keeps the words only where it asks the same question of
     them — "In honor of" written for one kiddush is still the answer for
     another, and is no answer to a yahrzeit's name, and an item that asks
     nothing takes words nobody would see sent with the gift. */
  const chooseItem = (id: string) => {
    discardCheckout()
    const asks = findDonateItem(items, id)?.descriptionPrompt ?? null
    if (asks === null || asks !== prompt) {
      setWords('')
      setAsked(false)
    }
    if (id !== picked) {
      dropPrice()
      setOften(null)
      setPendingOften(null)
    }
    setPicked(id)
    setReopened(null)
  }

  /* A figure held from a priced item is the item's price, not one the donor
     named — a stored answer carries it the same way — so it goes with the
     item rather than standing in for an amount nobody was asked. */
  const dropPrice = () => {
    if (priced !== null) setNamed(null)
  }

  /* `keep` marks the answer still on screen when the question reopens at the
     level it was picked from; reopened higher up the menu, the level shown
     does not hold it and nothing is marked. */
  const reopen = (question: 'branch' | 'item', keep = true) => {
    discardCheckout()
    setPendingItem(question === 'item' && keep ? picked : null)
    setReopened(question)
  }

  const submit = () => {
    if (!ready || giftCents === null) return
    /* The answers stored are the ones being paid for, and only those: a
       membership tried first and left would otherwise be what the thank-you
       names after a reload. */
    if (!kiosk) forgetMembership(orgId)
    rememberDonateAnswers(
      orgId,
      {
        branch: GIFT,
        open,
        chargeItemId: item?.id ?? null,
        forLabel,
        description: words.trim(),
        noteLabel: noteSaid,
        amountCents: giftCents,
        often: asksOften ? 'once' : undefined,
        coverFee,
        saveCard: kiosk ? false : saveCard,
        donorName: name.trim(),
        donorEmail: email.trim(),
      },
      kiosk,
    )
    checkout.mutate({
      shortId: orgId,
      amountCents: giftCents,
      coverFee,
      chargeItemId: item?.id,
      description: words.trim() || undefined,
      saveCard: kiosk ? false : saveCard,
      donorName: name.trim(),
      donorEmail: email.trim() || undefined,
      kiosk: kiosk || undefined,
    })
  }

  /* The question under a gift: what leads somewhere sits above what can be
     answered, because a row that opens and a row that settles are different
     acts and a set mixing them reads as one. The shul's own order holds within
     each. */
  const opens = level.filter((i) => i.children.length > 0)
  const answers: ChoiceOption[] = [
    /* The parent is chargeable, and that IS the escape hatch out of a price
       list: a kiddush at a figure the donor names. It carries no price, which
       is what tells it apart from the priced children beneath it. */
    ...(here
      ? [
          {
            value: here.id,
            label: here.label,
            description: here.note ?? undefined,
            amount:
              here.defaultCents !== null ? money(here.defaultCents) : undefined,
          },
        ]
      : []),
    ...level
      .filter((i) => i.children.length === 0)
      .map((i) => ({
        value: i.id,
        label: i.label,
        description: i.note ?? undefined,
        amount: i.defaultCents !== null ? money(i.defaultCents) : undefined,
      })),
  ]
  const ask = here ? here.label : MENU_ASK

  return (
    <PublicShell
      name={o.name}
      nameHe={o.nameHe}
      language={o.publicLanguage}
      orgId={o.shortId}
      acceptsDonations={o.acceptsDonations}
      contact={o}
      current="donate"
      onLogin={onLogin}
      kiosk={kiosk}
    >
      {paid ? (
        <DonateThanks
          orgId={o.shortId}
          orgName={o.name}
          thanks={thanksFor({
            standing:
              thankedStanding?.checkoutId === completedCheckoutId
                ? thankedStanding
                : null,
            membership: thankedMembership,
            gift: thankedGift,
            on: shulToday ?? readerToday,
          })}
          onAnother={() => {
            showOutcome(null)
            setThankedStanding(null)
            setKept(null)
            setKeptMembership(null)
            reset()
          }}
          kiosk={kiosk}
        />
      ) : (
        <GiveFrame
          head={
            <>
              <h1 className="give__title">{donateTitle(to)}</h1>
              {/* The shul's name is the masthead's, so the heading names the
                  act. A general gift counts toward no drive, so there is no
                  progress to report beside it. */}
              {o.acceptsDonations ? (
                <p className="give__lead">
                  Your receipt is emailed once the payment goes through.
                </p>
              ) : null}
            </>
          }
          other={kiosk ? [] : o.otherWaysToGive}
        >
          {/* A campaign link that answers to nothing still reaches the org: a
              donor holding an old flyer is offered every way to give rather
              than a dead end. */}
          {missedCampaign ? (
            <Notice tone="info">
              That campaign link was not found.
              {o.acceptsDonations ? ' You can still give here.' : ''}
            </Notice>
          ) : null}
          {!o.acceptsDonations ? (
            <DonateNotAccepting orgShortId={o.shortId} kiosk={kiosk} />
          ) : (
            <>
              {/* What the page knows of the last payment comes first. Until
                  it knows the payment did not go through, the answers are held
                  out of sight; once it knows, every answer stands and paying
                  is the one thing to try again. */}
              <ReturnedCheckoutNotice
                state={returned}
                unpaid={`Your payment has not completed, and nothing was charged. ${again}`}
                expired={`This payment session expired and nothing was charged. ${again}`}
              />
              <PaymentFlow hidden={holdsThePayment(returned)}>
                {/* Every question already answered, each with the way back to
                  it. A reopened question hides its own line and every line
                  below it until it is answered again. */}
                {on !== null && sole === null && reopened !== 'branch' && (
                  <AnswerLine
                    appearance="page"
                    label={account ? 'Here to' : 'Giving to'}
                    value={branches.find((b) => b.key === on)?.label ?? ''}
                    changeLabel={
                      account
                        ? 'Change what you came to do'
                        : 'Change what this gift is for'
                    }
                    onChange={() => reopen('branch')}
                  />
                )}
                {(!item || reopened === 'item') &&
                  reopened !== 'branch' &&
                  open.map((id, depth) => {
                    const under = findDonateItem(items, id)
                    /* An item the shul has withdrawn since the answers were
                     stored names nothing, so there is no line to draw and the
                     question below is asked again. */
                    if (!under) return null
                    return (
                      <AnswerLine
                        appearance="page"
                        key={id}
                        label="Under"
                        value={under.label}
                        changeLabel={`Change ${under.label}`}
                        onChange={() => {
                          setOpen(open.slice(0, depth))
                          reopen('item', false)
                        }}
                      />
                    )
                  })}
                {item && on === GIFT && itemWasChosen && reopened === null && (
                  <AnswerLine
                    appearance="page"
                    label="Gift for"
                    value={item.label}
                    changeLabel="Change what this gift is for"
                    onChange={() => reopen('item')}
                  />
                )}
                {noteSaid && asked && reopened === null && (
                  <AnswerLine
                    appearance="page"
                    words={words.trim().length > 0}
                    label={noteSaid}
                    value={words.trim() || 'No note'}
                    changeLabel={`Change your answer to “${noteAsk}”`}
                    onChange={() => {
                      discardCheckout()
                      setAsked(false)
                    }}
                  />
                )}
                {asksOften && often !== null && reopened === null && (
                  <AnswerLine
                    appearance="page"
                    label="How often"
                    value={OFTEN.find((x) => x.value === often)?.label ?? ''}
                    changeLabel="Change how often you give"
                    onChange={() => {
                      discardCheckout()
                      setPendingOften(often)
                      setOften(null)
                    }}
                  />
                )}
                {asksAmount && named !== null && stage === 'donor' && (
                  <AnswerLine
                    appearance="page"
                    label="Amount"
                    value={money(named)}
                    figure
                    detail={
                      covers ? 'Plus the card processing cost' : undefined
                    }
                    changeLabel={
                      covers
                        ? 'Change the amount, or whether you cover the card cost'
                        : 'Change the amount'
                    }
                    onChange={() => {
                      discardCheckout()
                      setTyped(String(named / 100))
                      setNamed(null)
                    }}
                  />
                )}

                {stage === 'branch' && (
                  <Question ref={stepRef} ask={MENU_ASK} appearance="page">
                    <BranchList appearance="page">
                      {branches.map((b) => (
                        <BranchRow
                          key={b.key}
                          label={b.label}
                          note={b.note}
                          onSelect={() => chooseBranch(b.key)}
                        />
                      ))}
                      {driveRows.map((d) => (
                        <BranchRow
                          key={d.key}
                          label={d.label}
                          note={d.note}
                          render={<a href={kioskHref(d.href, kiosk)} />}
                        />
                      ))}
                    </BranchList>
                  </Question>
                )}

                {/* Hidden rather than unmounted while the branch is asked
                  again, so a donor who picks membership a second time finds
                  their answers there. The region takes focus only while it
                  is the question in hand. */}
                {membership && shulToday && (
                  <HeldAnswers held={reopened === 'branch'}>
                    <MembershipBranch
                      orgId={o.shortId}
                      tiers={offered}
                      today={shulToday}
                      stepRef={reopened === 'branch' ? undefined : stepRef}
                      held={reopened === 'branch'}
                      onCheckoutComplete={completeMembership}
                    />
                  </HeldAnswers>
                )}

                {account && reopened !== 'branch' && (
                  <AccountLinkRequest orgId={o.shortId} stepRef={stepRef} />
                )}

                {!elsewhere && stage === 'menu' && (
                  <Question ref={stepRef} ask={ask} appearance="page">
                    {/* The question is asked ONCE, by the region holding it. The
                      rows that open and the options that answer are two kinds
                      of reply to it, not two questions. */}
                    {opens.length > 0 && (
                      <BranchList appearance="page">
                        {opens.map((i) => (
                          <BranchRow
                            key={i.id}
                            label={i.label}
                            note={i.note ?? undefined}
                            onSelect={() => {
                              setOpen([...open, i.id])
                              setPendingItem(null)
                            }}
                          />
                        ))}
                      </BranchList>
                    )}
                    {answers.length > 0 && (
                      <>
                        {/* Picking does not advance. A radio group moves AND
                          selects on an arrow key, so a set that committed on
                          selection could never be read past its second option
                          from the keyboard — and the answer would be gone
                          before it could be checked. */}
                        <Choice
                          ariaLabel={ask}
                          options={answers}
                          value={pendingItem ?? ''}
                          onValueChange={setPendingItem}
                        />
                        <Button
                          variant="secondary"
                          disabled={pendingItem === null}
                          onClick={() => pendingItem && chooseItem(pendingItem)}
                        >
                          Continue
                        </Button>
                      </>
                    )}
                  </Question>
                )}

                {!elsewhere && stage === 'words' && noteAsk && (
                  <Question ref={stepRef} ask={noteAsk} appearance="page">
                    {/* Picking the item was the answer; the words are the
                      donor's to add or leave, and the label says so rather
                      than letting an empty box read as one that must be
                      filled. The box grows with them, so a long dedication
                      is read whole while it is written. */}
                    <Field label="Your words" optional>
                      <Textarea
                        grow
                        rows={2}
                        value={words}
                        maxLength={500}
                        onChange={(e) => setWords(e.target.value)}
                      />
                    </Field>
                    <Button variant="secondary" onClick={() => setAsked(true)}>
                      Continue
                    </Button>
                  </Question>
                )}

                {!elsewhere && stage === 'often' && (
                  <HowOftenStep
                    stepRef={stepRef}
                    held={pendingOften}
                    onAnswer={chooseOften}
                  />
                )}

                {!elsewhere && stage === 'amount' && (
                  <DonateAmountStep
                    stepRef={stepRef}
                    orgId={o.shortId}
                    to={to}
                    suggestedAmountsCents={o.suggestedAmountsCents}
                    minGiftCents={o.minGiftCents}
                    maxGiftCents={o.maxGiftCents}
                    typed={typed}
                    onTyped={setTyped}
                    pending={pending}
                    boundsId={boundsId}
                    forLabel={forLabel}
                    donorName={name}
                    donorEmail={email}
                    coverFee={coverFee}
                    onCoverFee={setCoverFee}
                    onAmount={setNamed}
                    onPledgeComplete={completePledge}
                    standing={standing !== null}
                    offersPlan={givingChoiceOffered('installment', kiosk)}
                  />
                )}

                {!elsewhere &&
                  stage === 'donor' &&
                  standing !== null &&
                  item &&
                  giftCents !== null && (
                    <StandingGiftStep
                      stepRef={stepRef}
                      orgId={o.shortId}
                      itemId={item.id}
                      forLabel={forLabel}
                      amountCents={giftCents}
                      cadence={standing}
                      today={shulToday ?? readerToday}
                      startsOn={startsOn ?? shulToday ?? readerToday}
                      onStartsOn={setStartsOn}
                      name={name}
                      onName={setName}
                      email={email}
                      onEmail={setEmail}
                      onComplete={completeStanding}
                    />
                  )}

                {!elsewhere &&
                  stage === 'donor' &&
                  standing === null &&
                  giftCents !== null && (
                    <Question
                      ref={stepRef}
                      ask="Who is giving?"
                      appearance="page"
                    >
                      <DonorFields
                        kiosk={kiosk}
                        name={name}
                        onName={setName}
                        email={email}
                        onEmail={setEmail}
                      />

                      {/* Where the figure came with what it was for, the card
                      cost is decided here, beside the total; where the donor
                      named one, it was decided with the figure. */}
                      {!asksAmount && (
                        <SettingBlock
                          title="Cover the card processing cost"
                          description={coverFeeSays(giftCents, to)}
                        >
                          <Toggle
                            checked={coverFee}
                            onChange={setCoverFee}
                            label="Cover the card processing cost"
                            hideLabel
                          />
                        </SettingBlock>
                      )}

                      {/* On by default, and the one setting here that is: the card
                      frame discloses that the shul may charge this card again,
                      and a disclosure nobody can decline is not one. Asked
                      here rather than beside the card, because it is fixed
                      when the checkout opens: it decides whom Stripe keeps the
                      card for, and changing it under the card would mean a new
                      checkout and a card field emptied of what was typed. */}
                      {!kiosk ? (
                        <SettingBlock
                          title="Keep my card for next time"
                          info="We can use it for what you pay next. It is never charged without you setting that up."
                        >
                          <Toggle
                            checked={saveCard}
                            onChange={setSaveCard}
                            label="Keep my card for next time"
                            hideLabel
                          />
                        </SettingBlock>
                      ) : null}

                      <div className="donate__total">
                        <TotalLine
                          label={forLabel ?? 'Your gift'}
                          amount={money(giftCents)}
                        />
                        {covers && (
                          <TotalLine
                            label="Card processing"
                            amount={money(feeCents)}
                          />
                        )}
                        <TotalLine
                          label="Total"
                          amount={money(chargedCents)}
                          emphasis="total"
                        />
                      </div>

                      {checkout.error && (
                        <Notice tone="error">{checkout.error.message}</Notice>
                      )}
                      <Button
                        onClick={submit}
                        disabled={!ready || checkout.isPending}
                      >
                        {continueLabel(checkout.isPending)}
                      </Button>
                      <p className="donate__fine">
                        Next, your card. Nothing is charged yet.
                      </p>
                      {checkout.data && checkout.variables && (
                        <PaymentStep
                          orgId={orgId}
                          card={checkout.data.card}
                          what={forLabel ?? `A gift to ${to}`}
                          who={`Giving as ${[
                            checkout.variables.donorName,
                            checkout.variables.donorEmail,
                          ]
                            .filter(Boolean)
                            .join(', ')}`}
                          amount={money(checkoutTotalCents ?? chargedCents)}
                          amountNote={cardCostNote(
                            (checkoutTotalCents ?? chargedCents) -
                              checkout.variables.amountCents,
                          )}
                          payLabel={`Pay ${money(checkoutTotalCents ?? chargedCents)}`}
                          confirmation={confirmation}
                          onCardChange={reportKioskActivity}
                          onConfirmingChange={kioskBusy.setBusy}
                          onBack={discardCheckout}
                        />
                      )}
                    </Question>
                  )}
              </PaymentFlow>
            </>
          )}
        </GiveFrame>
      )}
    </PublicShell>
  )
}
